import React, { useMemo, useState } from "react";
import { Plus, X, UserRound, UsersRound } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const PRICE_FACTOR = 130; // approx home price per $1 of allowable monthly housing payment
const DTI = 0.43;

const estimate = (annualIncome, monthlyDebt) => {
  const allowable = (annualIncome / 12) * DTI - monthlyDebt;
  if (allowable <= 0) return 0;
  const price = allowable * PRICE_FACTOR;
  return Math.max(0, Math.round(price / 5000) * 5000);
};

const BuyerCard = ({ index, income, debt, onIncome, onDebt, onRemove }) => (
  <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
    <div className="mb-5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EAF9EA] text-sm font-bold text-[#25862F]">
          {index}
        </span>
        <h3 className="font-heading text-lg font-bold text-slate-900">
          Buyer {index} {index === 1 ? "(You)" : ""}
        </h3>
      </div>
      {onRemove && (
        <button
          data-testid="remove-second-buyer-btn"
          onClick={onRemove}
          className="rounded-full p-1.5 text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Remove second buyer"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium text-slate-700">Annual Income</Label>
        <div className="relative mt-1.5">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">$</span>
          <Input
            data-testid={`buyer${index}-income-input`}
            type="number"
            min="0"
            placeholder="80,000"
            value={income}
            onChange={(e) => onIncome(e.target.value)}
            className="h-12 rounded-xl border-slate-200 pl-8 text-base"
          />
        </div>
      </div>
      <div>
        <Label className="text-sm font-medium text-slate-700">Monthly Debt Payments</Label>
        <div className="relative mt-1.5">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">$</span>
          <Input
            data-testid={`buyer${index}-debt-input`}
            type="number"
            min="0"
            placeholder="500"
            value={debt}
            onChange={(e) => onDebt(e.target.value)}
            className="h-12 rounded-xl border-slate-200 pl-8 text-base"
          />
        </div>
      </div>
    </div>
  </div>
);

const Calculator = () => {
  const [b1Income, setB1Income] = useState("80000");
  const [b1Debt, setB1Debt] = useState("500");
  const [hasSecond, setHasSecond] = useState(false);
  const [b2Income, setB2Income] = useState("65000");
  const [b2Debt, setB2Debt] = useState("400");

  const alone = useMemo(() => estimate(Number(b1Income) || 0, Number(b1Debt) || 0), [b1Income, b1Debt]);
  const together = useMemo(
    () =>
      estimate(
        (Number(b1Income) || 0) + (Number(b2Income) || 0),
        (Number(b1Debt) || 0) + (Number(b2Debt) || 0)
      ),
    [b1Income, b1Debt, b2Income, b2Debt]
  );

  return (
    <section id="calculator" className="bg-[#F7FCF7] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#3CC54F]/40 bg-white px-4 py-1.5 font-heading text-sm font-bold text-[#25862F]">
            <UsersRound className="h-3.5 w-3.5" />
            <span className="text-[#3CC54F]">Dubble</span><span className="text-[#3CABDA]">Upp</span> Calculator
          </span>
          <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            See What a Second Buyer Does to Your Buying Power
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            Enter your income and monthly debt, then add a second buyer to see how combining buying
            power changes what you can afford with 3.5% down.
          </p>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {/* Inputs */}
          <div className="space-y-5">
            <BuyerCard
              index={1}
              income={b1Income}
              debt={b1Debt}
              onIncome={setB1Income}
              onDebt={setB1Debt}
            />
            {hasSecond ? (
              <BuyerCard
                index={2}
                income={b2Income}
                debt={b2Debt}
                onIncome={setB2Income}
                onDebt={setB2Debt}
                onRemove={() => setHasSecond(false)}
              />
            ) : (
              <button
                data-testid="add-second-buyer-btn"
                onClick={() => setHasSecond(true)}
                className="group flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#3CC54F]/60 bg-white/60 px-6 py-7 transition-colors duration-200 hover:border-[#2FA33F] hover:bg-[#EAF9EA]/60"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#3CC54F] text-white transition-transform duration-200 group-hover:scale-110">
                  <Plus className="h-4 w-4" />
                </span>
                <span className="text-base font-semibold text-slate-800">Add a Second Buyer</span>
              </button>
            )}
          </div>

          {/* Results */}
          <div className="flex flex-col rounded-3xl bg-white p-8 shadow-xl shadow-slate-900/5 ring-1 ring-slate-100">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Estimated Buying Power · 3.5% Down
            </p>

            <div className="mt-6 rounded-2xl bg-slate-50 p-6">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
                <UserRound className="h-4 w-4" /> Buying Alone
              </div>
              <p data-testid="result-alone" className="mt-2 font-heading text-4xl font-extrabold text-slate-900">
                {fmt(alone)}
              </p>
              <p className="mt-1 text-sm text-slate-500">3.5% down: {fmt(Math.round(alone * 0.035))}</p>
            </div>

            {hasSecond ? (
              <div className="mt-4 rounded-2xl bg-[#EAF9EA] p-6 ring-2 ring-[#3CC54F]/50">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#25862F]">
                  <UsersRound className="h-4 w-4" /> Buying Together
                </div>
                <p data-testid="result-together" className="mt-2 font-heading text-5xl font-extrabold text-[#1E6B26]">
                  {fmt(together)}
                </p>
                <p className="mt-1 text-sm text-[#25862F]">
                  3.5% down: {fmt(Math.round(together * 0.035))} — that's{" "}
                  <span className="font-bold">{fmt(Math.max(0, together - alone))}</span> more home
                </p>
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-dashed border-slate-300 p-6 text-slate-500">
                <UsersRound className="h-5 w-5 shrink-0" />
                <p className="text-sm">Add a second buyer to see what buying together could unlock.</p>
              </div>
            )}

            <p className="mt-auto pt-6 text-xs leading-relaxed text-slate-400">
              Estimates only, for illustration. Assumes an FHA-style 3.5% down payment, a ~43%
              debt-to-income ratio, and typical rates, taxes, insurance, and mortgage insurance.
              Actual qualification depends on credit, rates, and lender review with your DubbleUpp
              Mortgage Expert.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
